import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, History as HistoryIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useFieldAccess, FlacScopeBanner } from "../hooks/useFieldAccess";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ClientOverview from "../components/ClientOverview";
import ClientImportHistory from "../components/ClientImportHistory";
import ActivityFeed from "../components/ActivityFeed";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canEdit } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "overview") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const flac = useFieldAccess(client);

  const canValidate = user && (user.role === "admin" || user.role === "backoffice");

  const fetchClient = async () => {
    try {
      const r = await axios.get(`${API_URL}/clients/${id}`);
      setClient(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Client negăsit");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="client-detail">
      <FlacScopeBanner flac={flac} entity="acest client" />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Iter51 Etapa 9 — TabsList ascuns pe Overview (match pattern SaleDetail) */}
        {activeTab !== "overview" && (
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="tab-client-overview">Overview</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-client-history">
              <HistoryIcon className="h-3.5 w-3.5 mr-1.5" />
              Istoric Importuri
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-client-activity">
              <HistoryIcon className="h-3.5 w-3.5 mr-1.5" />
              Activitate
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="overview" className="mt-0">
          <ClientOverview
            client={client}
            onChange={fetchClient}
            canEdit={canEdit ? canEdit() : true}
            canValidateContact={canValidate}
            flac={flac}
          />
          {/* Quick-nav discret la final, ca pe SaleDetail */}
          <div className="mt-6 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500" data-testid="client-overview-quick-nav">
            <span className="font-medium uppercase tracking-wider text-slate-400">Secțiuni avansate:</span>
            <button type="button" onClick={() => setActiveTab("history")} className="hover:text-blue-600 hover:underline flex items-center gap-1" data-testid="quick-nav-history">
              <HistoryIcon className="h-3 w-3" /> Istoric Importuri
            </button>
            <span className="text-slate-300">·</span>
            <button type="button" onClick={() => setActiveTab("activity")} className="hover:text-blue-600 hover:underline flex items-center gap-1" data-testid="quick-nav-activity">
              <HistoryIcon className="h-3 w-3" /> Activitate
            </button>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-0">
          <ClientImportHistory clientId={id} />
        </TabsContent>

        <TabsContent value="activity" className="mt-0">
          {activeTab === "activity" && <ActivityFeed entityType="client" entityId={id} />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientDetail;
